import { useEffect, useRef } from 'react';
import { StyledSelect } from '~/components/dropdowns/select.style';
import { useCountiesForZip } from '~/hooks';
import { EventName, Logger } from '~/utils/logger';

interface CountyForZipProps {
  enabled: boolean;
  zipCode: string;
  value?: number;
  onChange: (
    value: number | string | undefined,
    meta: { isValid: boolean; name: 'countyId' | 'countyName' },
  ) => void;
}

export const CountyForZip = ({ enabled, zipCode, value, onChange }: CountyForZipProps) => {
  const { counties, loading, error } = useCountiesForZip(zipCode, enabled);
  const reportedCountyLookupError = useRef<string | null>(null);
  const reportedCountySelection = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !error) return;

    const errorKey = `${zipCode}:${error.message}`;
    if (reportedCountyLookupError.current === errorKey) return;
    reportedCountyLookupError.current = errorKey;

    Logger.logEvent({ countyLookupError: error.message, zipCode }, EventName.UtilityScreen);
  }, [enabled, error, zipCode]);

  useEffect(() => {
    if (!enabled) return;

    const reportCounty = (countyId: number | undefined, isValid: boolean, countyName = '') => {
      const reportKey = `${countyId ?? 'none'}:${isValid}`;
      if (reportedCountySelection.current === reportKey) return;
      reportedCountySelection.current = reportKey;
      onChange(countyId, { isValid, name: 'countyId' });
      onChange(countyName, { isValid: true, name: 'countyName' });
    };

    if (error || (!loading && counties.length === 0)) {
      reportCounty(undefined, true);
      return;
    }

    if (loading) {
      reportCounty(undefined, false);
      return;
    }

    if (counties.length === 1) {
      const [singleCounty] = counties;
      reportCounty(singleCounty.id, true, singleCounty.name);
      return;
    }

    const selectedCounty = counties.find(county => county.id === value);
    reportCounty(selectedCounty?.id, !!selectedCounty, selectedCounty?.name ?? '');
  }, [counties, enabled, error, loading, onChange, value]);

  if (!enabled || (!loading && counties.length === 0)) return null;

  const singleCounty = counties.length === 1 ? counties[0] : undefined;
  const selectedCountyId = singleCounty?.id ?? value;
  const selectedValue = selectedCountyId != null ? String(selectedCountyId) : '';

  return (
    <StyledSelect
      disabled={loading || !!singleCounty}
      id="county-for-zip"
      label="County"
      loading={loading}
      minHeight="auto"
      name="countyId"
      onChange={selectedId => {
        const selectedCounty = counties.find(county => county.id === Number(selectedId));
        onChange(selectedCounty?.id, { isValid: !!selectedCounty, name: 'countyId' });
        onChange(selectedCounty?.name ?? '', { isValid: true, name: 'countyName' });
      }}
      options={counties.map(county => ({ text: county.name, value: String(county.id) }))}
      placeholder="Select your county"
      required={!singleCounty}
      value={selectedValue}
    />
  );
};
